import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Sprout, Trash2, Users, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import * as pipelineValidator from "@/services/pipelineValidator";
import type { PVSeedResult } from "@/types";

const TIMEZONE_OPTIONS = [
	"UTC",
	"Asia/Dhaka",
	"Asia/Kolkata",
	"America/New_York",
	"America/Los_Angeles",
	"Europe/London",
	"Australia/Sydney",
];

interface SeederPanelProps {
	organizationId: string;
	year: number;
	month: number;
	onOrgCreated?: (orgId: string) => void;
}

export function SeederPanel({
	organizationId,
	year,
	month,
	onOrgCreated,
}: SeederPanelProps) {
	const { toast } = useToast();
	const [selectedScenarios, setSelectedScenarios] = useState<Set<string>>(
		new Set(),
	);
	const [seedResult, setSeedResult] = useState<PVSeedResult | null>(null);
	const [initialized, setInitialized] = useState(false);
	const [timezone, setTimezone] = useState("UTC");

	const { data: scenarios, isLoading: scenariosLoading } = useQuery({
		queryKey: ["pv-scenarios"],
		queryFn: pipelineValidator.getScenarios,
	});

	// Select all scenarios by default once loaded
	if (scenarios && !initialized) {
		setSelectedScenarios(new Set(scenarios.map((s) => s.name)));
		setInitialized(true);
	}

	const seedMutation = useMutation({
		mutationFn: () => {
			const params: Parameters<typeof pipelineValidator.seedData>[0] = {
				period: { year, month },
				scenarios: Array.from(selectedScenarios),
			};
			if (organizationId.trim()) {
				params.organizationId = organizationId;
			} else {
				params.timezone = timezone;
			}
			return pipelineValidator.seedData(params);
		},
		onSuccess: (data) => {
			setSeedResult(data);
			if (!organizationId.trim() && data.organizationId) {
				onOrgCreated?.(data.organizationId);
			}
			toast({
				title: "Seed complete",
				description: `Created ${data.employeesCreated.length} employees across ${data.scenariosSeeded.length} scenarios`,
			});
		},
		onError: (err: Error) => {
			toast({
				title: "Seed failed",
				description: err.message,
				variant: "destructive",
			});
		},
	});

	const cleanupOrgId = seedResult?.organizationId || organizationId;

	const cleanupMutation = useMutation({
		mutationFn: () => pipelineValidator.cleanupData(cleanupOrgId),
		onSuccess: (data) => {
			setSeedResult(null);
			toast({
				title: "Cleanup complete",
				description: `Deleted ${data.usersDeleted} users, ${data.attendanceDaysDeleted} attendance days, ${data.timesheetsDeleted} timesheets`,
			});
		},
		onError: (err: Error) => {
			toast({
				title: "Cleanup failed",
				description: err.message,
				variant: "destructive",
			});
		},
	});

	function toggleScenario(name: string) {
		setSelectedScenarios((prev) => {
			const next = new Set(prev);
			if (next.has(name)) {
				next.delete(name);
			} else {
				next.add(name);
			}
			return next;
		});
	}

	const canSeed = selectedScenarios.size > 0;
	const canCleanup = cleanupOrgId.trim() !== "";
	const isSandboxMode = organizationId.trim() === "";

	return (
		<div className="space-y-4">
			{isSandboxMode && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<Building2 className="h-4 w-4" />
							Sandbox Mode
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="text-sm text-muted-foreground">
							No Organization ID provided. A new sandbox org will
							be created when you seed.
						</p>
						<div className="space-y-2">
							<Label htmlFor="timezone-select">Timezone</Label>
							<select
								id="timezone-select"
								className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
								value={timezone}
								onChange={(e) => setTimezone(e.target.value)}
							>
								{TIMEZONE_OPTIONS.map((tz) => (
									<option key={tz} value={tz}>
										{tz}
									</option>
								))}
							</select>
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Sprout className="h-5 w-5" />
						Scenarios
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					{scenariosLoading && (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" />
							Loading scenarios...
						</div>
					)}
					{scenarios?.map((scenario) => (
						<div
							key={scenario.name}
							className="flex items-start gap-3"
						>
							<Checkbox
								id={`scenario-${scenario.name}`}
								checked={selectedScenarios.has(scenario.name)}
								onCheckedChange={() =>
									toggleScenario(scenario.name)
								}
							/>
							<div className="grid gap-0.5 leading-none">
								<Label
									htmlFor={`scenario-${scenario.name}`}
									className="font-medium cursor-pointer"
								>
									{scenario.name}
								</Label>
								<p className="text-sm text-muted-foreground">
									{scenario.description}
								</p>
							</div>
						</div>
					))}
				</CardContent>
			</Card>

			<div className="flex gap-3">
				<Button
					onClick={() => seedMutation.mutate()}
					disabled={!canSeed || seedMutation.isPending}
				>
					{seedMutation.isPending ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<Sprout className="mr-2 h-4 w-4" />
					)}
					{isSandboxMode ? "Create Sandbox & Seed" : "Seed Data"}
				</Button>
				<Button
					variant="destructive"
					onClick={() => cleanupMutation.mutate()}
					disabled={!canCleanup || cleanupMutation.isPending}
				>
					{cleanupMutation.isPending ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<Trash2 className="mr-2 h-4 w-4" />
					)}
					Cleanup
				</Button>
			</div>

			{seedResult && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<Users className="h-4 w-4" />
							Seed Result — {seedResult.employeesCreated.length}{" "}
							employees
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{seedResult.organizationName && (
							<div className="rounded-md border p-3 space-y-1 text-sm bg-muted/50">
								<div className="flex justify-between">
									<span className="font-medium">
										Organization
									</span>
									<span>
										{seedResult.organizationName}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="font-medium">Org ID</span>
									<code className="text-xs bg-muted px-1.5 py-0.5 rounded">
										{seedResult.organizationId}
									</code>
								</div>
								{seedResult.ownerUserId && (
									<div className="flex justify-between">
										<span className="font-medium">
											Owner User ID
										</span>
										<code className="text-xs bg-muted px-1.5 py-0.5 rounded">
											{seedResult.ownerUserId}
										</code>
									</div>
								)}
							</div>
						)}
						<div className="space-y-1 text-sm">
							{seedResult.employeesCreated.map((emp) => (
								<div
									key={emp.userId}
									className="flex justify-between"
								>
									<span>{emp.name}</span>
									<span className="text-muted-foreground">
										{emp.scenario}
									</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
